import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, Search, Trash2, PackagePlus, Check, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const NONE = "__none";
const VM_TYPES = ["MDVR", "Dashcam", "Accesorios de Videovigilancia Móvil"];
export const NO_MODEL = "Sin modelo especificado";

const useCatalog = () => {
  const [types, setTypes] = useState([]);
  const [models, setModels] = useState([]);
  const reload = () => {
    api.get("/catalog/types", { params: { active: true } }).then((r) => setTypes(r.data));
    api.get("/catalog/models", { params: { active: true } }).then((r) => setModels(r.data));
  };
  useEffect(reload, []);
  const byType = useMemo(() => models.reduce((acc, m) => ((acc[m.type_id] ||= []).push(m), acc), {}), [models]);
  return { types, byType, reload };
};

export const toPayload = (lines) =>
  lines.map((l) => l.manual
    ? { line_id: l.line_id || null, manual: true, type_name: l.type_name, brand: l.brand, model: l.model, description: l.description ?? null, quantity: l.quantity, notes: l.notes ?? null }
    : { line_id: l.line_id || null, type_id: l.type_id, model_id: l.model_id || null, brand: l.model_id ? null : l.brand || null, quantity: l.quantity, description: l.description ?? null, notes: l.notes ?? null });

const LineNotes = ({ l, i, disabled, onChange }) => (disabled
  ? (l.notes ? <div className="text-xs text-muted-foreground" data-testid={`equipment-notes-text-${i}`}>Observaciones del equipo: {l.notes}</div> : null)
  : <label className="block space-y-1"><span className="text-xs font-medium text-muted-foreground">Observaciones del equipo</span>
    <Textarea rows={2} value={l.notes || ""} onChange={(e) => onChange({ ...l, notes: e.target.value })} placeholder="Estado, condición encontrada o falla particular" className="text-sm" data-testid={`equipment-notes-${i}`} /></label>);

const EMPTY_MANUAL = { type_name: "", brand: "", model: "", description: "", quantity: 1, classification: "" };

const ClassSelect = ({ value, onChange }) => {
  const { meta } = useAuth();
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-11" data-testid="manual-class-select"><SelectValue placeholder="Clasificación (requerida si el tipo es nuevo)" /></SelectTrigger>
      <SelectContent>{(meta?.item_classes || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const contribute = async (m, bu, workOrderId) => {
  try {
    const { data } = await api.post("/catalog/contribute", { business_unit_code: bu, type_name: m.type_name, brand: m.brand, model: m.model, description: m.description, classification: m.classification || null, work_order_id: workOrderId || null });
    if (data.existed) toast.info("Equipo existente en catálogo: se reutilizó el registro.");
    else toast.success(data.model_created ? "Equipo nuevo agregado automáticamente al catálogo" : "Tipo nuevo agregado automáticamente al catálogo");
    return data;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
};

const BuSelect = ({ bu, setBu }) => {
  const { meta } = useAuth();
  return (
    <Select value={bu || ""} onValueChange={setBu}>
      <SelectTrigger className="h-11" data-testid="manual-bu-select"><SelectValue placeholder="Unidad de negocio *" /></SelectTrigger>
      <SelectContent>{(meta?.business_units || []).map((b) => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const catalogLine = (t, mdl, m) => ({
  key: `cat-${Date.now()}`, line_id: null, manual: false, type_id: t.id, type_name: t.name, business_unit_code: t.business_unit_code,
  model_id: mdl?.id || null, brand: mdl ? mdl.brand : m.brand.trim(), model: mdl?.model || "", unit: t.unit || "Pieza",
  quantity: Math.max(1, Number(m.quantity) || 1),
  description: m.description.trim() || mdl?.description || [t.name, m.brand.trim(), m.model.trim()].filter(Boolean).join(" "),
});

const ManualForm = ({ onAdd, buCode, workOrderId, onCatalogSaved }) => {
  const [m, setM] = useState(EMPTY_MANUAL);
  const [bu, setBu] = useState(buCode);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setM((s) => ({ ...s, [k]: e.target.value }));
  const invalid = !m.type_name.trim() || !bu;
  const add = async () => {
    setBusy(true);
    const data = await contribute(m, bu, workOrderId);
    setBusy(false);
    if (!data) return;
    onCatalogSaved();
    onAdd(catalogLine(data.type, data.model, m));
    setM(EMPTY_MANUAL);
  };
  return (
    <div className="space-y-2" data-testid="manual-equipment-form">
      <p className="text-xs text-muted-foreground" data-testid="manual-scope-hint">Si el equipo no existe en el catálogo se agrega automáticamente; si ya existe, se reutiliza. Sin modelo queda como "{NO_MODEL}".</p>
      <BuSelect bu={bu} setBu={setBu} />
      <Input value={m.type_name} onChange={set("type_name")} placeholder="Tipo / categoría *" className="h-11" data-testid="manual-type-input" />
      <ClassSelect value={m.classification} onChange={(v) => setM((s) => ({ ...s, classification: v }))} />
      <div className="grid grid-cols-2 gap-2">
        <Input value={m.brand} onChange={set("brand")} placeholder="Marca" className="h-11" data-testid="manual-brand-input" />
        <Input value={m.model} onChange={set("model")} placeholder="Modelo" className="h-11 font-mono" data-testid="manual-model-input" />
      </div>
      <Input value={m.description} onChange={set("description")} placeholder="Descripción" className="h-11" data-testid="manual-description-input" />
      <Input type="number" min={1} value={m.quantity} onChange={set("quantity")} placeholder="Cantidad" className="h-11 font-mono" data-testid="manual-quantity-input" />
      <Button type="button" className="h-11 w-full" disabled={invalid || busy} onClick={add} data-testid="manual-add-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Agregar equipo</Button>
    </div>
  );
};

const newLine = (type, model) => ({
  key: `${type.id}-${model?.id || "none"}-${Date.now()}`, line_id: null, type_id: type.id, type_name: type.name,
  business_unit_code: type.business_unit_code, model_id: model?.id || null, brand: model?.brand || "", model: model?.model || "",
  description: model?.description || type.description || type.name, unit: type.unit || "Pieza", quantity: 1,
});

const sameCombo = (l, typeId, modelId) => l.type_id === typeId && (l.model_id || null) === (modelId || null);

const PickOption = ({ type, model, count, onClick, showType }) => (
  <button type="button" onClick={onClick} data-testid={`picker-option-${type.id}-${model?.id || "none"}`}
    className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 text-left transition-colors hover:border-primary/50 hover:bg-secondary active:scale-[0.99]">
    <div className="min-w-0 flex-1">
      {showType && <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{type.name}</div>}
      <div className={cn("text-sm font-semibold", model ? "font-mono" : "italic text-muted-foreground")}>{model ? `${model.brand} ${model.model}` : NO_MODEL}</div>
      {model?.description && <div className="truncate text-xs text-muted-foreground">{model.description}</div>}
    </div>
    {count ? <span className="flex items-center gap-1 font-mono text-xs text-primary"><Check className="h-4 w-4" />{count}</span> : <Plus className="h-5 w-5 text-primary" />}
  </button>
);

const ModeTabs = ({ mode, setMode }) => (
  <div className="grid grid-cols-2 rounded-lg border border-border p-1" data-testid="picker-mode-tabs">
    {[["catalog", "Desde catálogo"], ["manual", "Captura manual"]].map(([v, l]) => (
      <button key={v} type="button" onClick={() => setMode(v)} data-testid={`picker-mode-${v}`}
        className={cn("rounded-md py-2 text-sm font-medium", mode === v ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>{l}</button>
    ))}
  </div>
);

const EquipmentPicker = ({ open, onOpenChange, buCode, types, byType, lines, onPick, allowManual, onManual, workOrderId, onCatalogSaved }) => {
  const { meta } = useAuth();
  const [bu, setBu] = useState(buCode);
  const [type, setType] = useState(null);
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("catalog");
  useEffect(() => {
    if (open) { setBu(buCode || meta?.business_units?.[0]?.code); setType(null); setQ(""); setMode("catalog"); }
  }, [open, buCode, meta]);

  const count = (tid, mid) => lines.filter((l) => sameCombo(l, tid, mid)).reduce((s, l) => s + l.quantity, 0);
  const buTypes = types.filter((t) => t.business_unit_code === bu || (bu === "videovigilancia_movil" && t.business_unit_code === "gps" && VM_TYPES.includes(t.name)));
  const ql = q.trim().toLowerCase();
  const found = ql
    ? buTypes.flatMap((t) => [
        ...(t.name.toLowerCase().includes(ql) ? [{ t, m: null }] : []),
        ...(byType[t.id] || []).filter((m) => `${t.name} ${m.brand} ${m.model} ${m.description}`.toLowerCase().includes(ql)).map((m) => ({ t, m })),
      ])
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg" data-testid="equipment-picker-dialog">
        <DialogHeader><DialogTitle className="font-display">Agregar equipamiento</DialogTitle></DialogHeader>
        {allowManual && <ModeTabs mode={mode} setMode={setMode} />}
        {mode === "manual" ? <div className="flex-1 overflow-y-auto"><ManualForm buCode={bu} workOrderId={workOrderId} onCatalogSaved={onCatalogSaved} onAdd={(l) => { onManual(l); onOpenChange(false); }} /></div> : <>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {(meta?.business_units || []).map((b) => (
            <button key={b.code} type="button" onClick={() => { setBu(b.code); setType(null); }} data-testid={`picker-bu-${b.code}`}
              className={cn("h-9 shrink-0 rounded-full border px-4 text-sm", bu === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground")}>{b.name}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar tipo, marca o modelo" className="h-11 pl-9" data-testid="picker-search-input" />
        </div>
        <div className="-mx-2 flex-1 space-y-1.5 overflow-y-auto px-2">
          {found && (found.length ? found.map(({ t, m }) => <PickOption key={`${t.id}-${m?.id}`} type={t} model={m} showType count={count(t.id, m?.id)} onClick={() => onPick(t, m)} />)
            : <p className="py-8 text-center text-sm text-muted-foreground">Sin resultados</p>)}
          {!found && !type && buTypes.map((t) => (
            <button key={t.id} type="button" onClick={() => setType(t)} data-testid={`picker-type-${t.id}`}
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 text-left transition-colors hover:border-primary/50">
              <span className="flex-1 text-sm font-medium">{t.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{(byType[t.id] || []).length} modelos</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
          {!found && type && (
            <>
              <button type="button" onClick={() => setType(null)} className="mb-1 flex items-center gap-1 text-sm text-primary" data-testid="picker-back-button"><ArrowLeft className="h-4 w-4" />{type.name}</button>
              <PickOption type={type} model={null} count={count(type.id, null)} onClick={() => onPick(type, null)} />
              {(byType[type.id] || []).map((m) => <PickOption key={m.id} type={type} model={m} count={count(type.id, m.id)} onClick={() => onPick(type, m)} />)}
            </>
          )}
        </div>
        <Button onClick={() => onOpenChange(false)} className="h-11" data-testid="picker-done-button">Listo</Button>
        </>}
      </DialogContent>
    </Dialog>
  );
};

const QtyControl = ({ value, onChange, disabled, testid }) => (
  <div className="flex items-center rounded-lg border border-border">
    <button type="button" disabled={disabled || value <= 1} onClick={() => onChange(value - 1)} className="flex h-10 w-10 items-center justify-center disabled:opacity-30" data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></button>
    <input type="number" min={1} value={value} disabled={disabled} onChange={(e) => onChange(Math.max(1, Number(e.target.value) || 1))}
      className="h-10 w-14 bg-transparent text-center font-mono text-sm outline-none [appearance:textfield]" data-testid={`${testid}-input`} />
    <button type="button" disabled={disabled} onClick={() => onChange(value + 1)} className="flex h-10 w-10 items-center justify-center disabled:opacity-30" data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></button>
  </div>
);

const ManualLineRow = ({ l, i, disabled, onChange, onRemove, allowNotes }) => {
  const set = (k) => (e) => onChange({ ...l, [k]: e.target.value });
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card/60 p-3" data-testid={`equipment-line-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">{l.type_name} <span className="ml-1 rounded border border-amber-500/30 px-1.5 py-0.5 text-[10px] text-amber-400" data-testid={`equipment-manual-badge-${i}`}>Manual</span></span>
        {!disabled && <button type="button" onClick={onRemove} className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-red-400" data-testid={`equipment-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      {disabled ? (
        <div className="text-sm"><span className="font-mono font-semibold">{[l.brand, l.model].filter(Boolean).join(" ") || NO_MODEL}</span> · {l.quantity} {l.unit}<div className="text-xs text-muted-foreground">{l.description}</div></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Input value={l.type_name} onChange={set("type_name")} placeholder="Tipo / categoría" className="h-10 text-sm" data-testid={`equipment-manual-type-${i}`} />
            <Input value={l.brand} onChange={set("brand")} placeholder="Marca" className="h-10 text-sm" data-testid={`equipment-manual-brand-${i}`} />
            <Input value={l.model} onChange={set("model")} placeholder="Modelo" className="h-10 font-mono text-sm" data-testid={`equipment-manual-model-${i}`} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={l.description || ""} onChange={set("description")} placeholder="Descripción" className="h-10 min-w-[180px] flex-1 text-sm" data-testid={`equipment-description-${i}`} />
            <QtyControl value={l.quantity} onChange={(q) => onChange({ ...l, quantity: q })} testid={`equipment-qty-${i}`} />
          </div>
        </>
      )}
      {allowNotes && <LineNotes l={l} i={i} disabled={disabled} onChange={onChange} />}
    </div>
  );
};

const LineRow = ({ l, i, models, disabled, allowDescription, allowNotes, onChange, onRemove }) => {
  const opts = [...(models || [])];
  if (l.model_id && !opts.find((m) => m.id === l.model_id)) opts.push({ id: l.model_id, brand: l.brand, model: l.model, description: l.description, inactive: true });
  const setModel = (v) => {
    const m = opts.find((x) => x.id === v);
    onChange({ ...l, model_id: m?.id || null, brand: m?.brand || "", model: m?.model || "", description: m?.description || l.type_name });
  };
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card/60 p-3" data-testid={`equipment-line-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">{l.type_name}</span>
        {!disabled && <button type="button" onClick={onRemove} className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-red-400" data-testid={`equipment-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[180px] flex-1">
          <Select value={l.model_id || NONE} onValueChange={(v) => setModel(v === NONE ? null : v)} disabled={disabled}>
            <SelectTrigger className="h-10" data-testid={`equipment-model-select-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>{NO_MODEL}</SelectItem>
              {opts.map((m) => <SelectItem key={m.id} value={m.id}>{m.brand} {m.model}{m.inactive ? " (inactivo)" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <QtyControl value={l.quantity} disabled={disabled} onChange={(q) => onChange({ ...l, quantity: q })} testid={`equipment-qty-${i}`} />
      </div>
      {allowDescription && !disabled ? (
        <Input value={l.description || ""} onChange={(e) => onChange({ ...l, description: e.target.value })} placeholder="Descripción que se mostrará en la OS" className="h-9 text-sm" data-testid={`equipment-description-${i}`} />
      ) : <div className="text-xs text-muted-foreground">{l.description}</div>}
      {allowNotes && <LineNotes l={l} i={i} disabled={disabled} onChange={onChange} />}
    </div>
  );
};

export const EquipmentEditor = ({ lines, onChange, disabled, buCode, allowDescription, allowManual, workOrderId, allowNotes }) => {
  const { types, byType, reload } = useCatalog();
  const [open, setOpen] = useState(false);
  const update = (i, l) => onChange(lines.map((x, idx) => (idx === i ? l : x)));
  const add = (type, model) => {
    const i = lines.findIndex((l) => !l.manual && sameCombo(l, type.id, model?.id));
    if (i >= 0) return update(i, { ...lines[i], quantity: lines[i].quantity + 1 });
    onChange([...lines, newLine(type, model)]);
  };
  return (
    <div className="space-y-2" data-testid="equipment-editor">
      {lines.length === 0 && <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">Sin equipos registrados</p>}
      {lines.map((l, i) => {
        const props = { l, i, disabled, allowNotes, onChange: (nl) => update(i, nl), onRemove: () => onChange(lines.filter((_, idx) => idx !== i)) };
        return l.manual ? <ManualLineRow key={l.line_id || l.key} {...props} />
          : <LineRow key={l.line_id || l.key} {...props} models={byType[l.type_id]} allowDescription={allowDescription} />;
      })}
      {!disabled && (
        <Button type="button" variant="outline" className="h-12 w-full border-dashed" onClick={() => setOpen(true)} data-testid="equipment-add-button">
          <PackagePlus className="mr-2 h-4 w-4" /> Agregar equipo
        </Button>
      )}
      <EquipmentPicker open={open} onOpenChange={setOpen} buCode={buCode} types={types} byType={byType} lines={lines} onPick={add}
        allowManual={allowManual} onManual={(l) => onChange([...lines, l])} workOrderId={workOrderId} onCatalogSaved={reload} />
    </div>
  );
};

export const EquipmentReadList = ({ lines }) =>
  lines?.length ? (
    <div className="divide-y divide-border rounded-lg border border-border" data-testid="equipment-read-list">
      {lines.map((l) => (
        <div key={l.line_id || l.catalog_id} className="flex items-center gap-3 px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l.type_name || l.category}{l.manual && <span className="ml-1.5 text-amber-400">· Manual</span>}</div>
            <div className={cn("text-sm font-semibold", l.model ? "font-mono" : "italic text-muted-foreground")}>{l.model ? `${l.brand} ${l.model}` : NO_MODEL}</div>
            <div className="truncate text-xs text-muted-foreground">{l.description}</div>
            {l.notes && <div className="whitespace-pre-wrap text-xs text-muted-foreground">Observaciones del equipo: {l.notes}</div>}
          </div>
          <span className="font-mono text-sm text-primary">{l.quantity} <span className="text-xs text-muted-foreground">{l.unit}</span></span>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm text-muted-foreground">Sin equipamiento registrado.</p>
  );
